/**
 * Build-time prerender entry (see `vite-prerender-plugin`).
 * Renders public routes to static HTML for crawlers and link previews.
 */

import './index.css'

import { StaticRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { QueryClientProvider } from '@tanstack/react-query'
import { Suspense } from 'react'
import { AuthProvider } from './contexts/AuthContext'
import { CurrencyProvider } from './contexts/CurrencyContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { AppRoutes } from './appRoutes'
import { createAppQueryClient } from './queryClient'
import { buildPrerenderHeadMarkup } from './utils/prerenderHead'

;(globalThis as { __TCGEASY_PRERENDER__?: boolean }).__TCGEASY_PRERENDER__ = true

const PageLoader = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '1.2rem',
      color: '#666',
    }}
  >
    Loading...
  </div>
)

function stripHeadInjectionDuplicates(html: string): string {
  return html
    .replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, '')
    .replace(/<meta\b[^>]*>/gi, '')
    .replace(/<link\b[^>]*rel\s*=\s*["']canonical["'][^>]*>/gi, '')
}

export async function prerender(data: { url: string }) {
  const helmetContext: Record<string, unknown> = {}
  const queryClient = createAppQueryClient()

  // react-dom/static waits for lazy routes. renderToString would stop at the Suspense fallback and
  // ship "Loading..." as the page.
  const { prerender: reactPrerender } = await import('react-dom/static')
  const { prelude } = await reactPrerender(
      <StaticRouter location={data.url}>
        <HelmetProvider context={helmetContext}>
          <ThemeProvider>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <CurrencyProvider>
                  <Suspense fallback={<PageLoader />}>
                    <AppRoutes />
                  </Suspense>
                </CurrencyProvider>
              </AuthProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </HelmetProvider>
      </StaticRouter>,
      // Without this, a long page is outlined behind its fallback and only filled in by a script.
      { progressiveChunkSize: Number.POSITIVE_INFINITY }
  )
  const html = stripHeadInjectionDuplicates(await new Response(prelude).text())

  const headBuilt = buildPrerenderHeadMarkup(data.url)

  return {
    html,
    head: {
      lang: 'en',
      title: headBuilt.title,
      elements: headBuilt.elements,
    },
  }
}
